import React, { useState, useEffect, useMemo } from 'react';
import dayjs from 'dayjs';

import {
    Box,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Grid,
    Paper,
    Checkbox,
    Autocomplete,
    styled,
    Snackbar,
    Alert,
    Typography,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    DialogContentText,
    Divider
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { TimeField } from '@mui/x-date-pickers/TimeField';

import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import RefreshIcon from '@mui/icons-material/Refresh';

import { DataGrid } from '@mui/x-data-grid';

import ActionService from "../services/ActionService";
import TableService from "../services/TableService";
import StakeholderService from "../services/StakeholderService";


const Item = styled(Paper)(({ theme }) => ({
    backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
    ...theme.typography.body2,
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
}));


const InsertAction = props => {
    const admin = props.user?.roles?.includes("ROLE_CLUB") ? false : true;
    const user = props.user;
    const today = dayjs();

    const initialActionState = {
        data: today.format("YYYY-MM-DD"),
        orario_inizio: null,
        orario_fine: null,
        tipologia: "",
        note: "",
        contatto_id: [],
    };

    // Stati Form Inserimento
    const [Action, setAction] = useState(initialActionState);
    const [startTime, setStartTime] = useState(dayjs().hour(0).minute(0).second(0).millisecond(0));
    const [endTime, setEndTime] = useState(dayjs().hour(0).minute(0).second(0).millisecond(0));

    // Stato Filtro Tabella (Data selezionata)
    const [filterDate, setFilterDate] = useState(today.format("YYYY-MM-DD"));

    // Stati Dati e UI
    const [stakeholders, setStakeholders] = useState([]);
    const [tables, setTables] = useState([]);
    const [todayActivities, setTodayActivities] = useState([]);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');

    // Stati Dialog Modifica
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [currentActionEdit, setCurrentActionEdit] = useState(null);
    const [editFormData, setEditFormData] = useState({ ...initialActionState, id: null });
    const [editStartTime, setEditStartTime] = useState(dayjs().hour(0).minute(0).second(0).millisecond(0));
    const [editEndTime, setEditEndTime] = useState(dayjs().hour(0).minute(0).second(0).millisecond(0));
    const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);

    const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
    const checkedIcon = <CheckBoxIcon fontSize="small" />;

    const [columnVisibilityModel, setColumnVisibilityModel] = useState({
        id: false,
        nome: admin,
        cognome: admin,
        codice: true,
        tipologia: true,
        orario_inizio: true,
        orario_fine: true,
    });

    useEffect(() => {
        setColumnVisibilityModel(prevModel => ({
            ...prevModel,
            nome: admin,
            cognome: admin,
        }));
    }, [admin]);

    useEffect(() => {
        retrieveTables();
        if (user && user.sede) {
            retrieveStakeholders(user.sede);
        }
    }, [user]);

    // Fondamentale: Ricarica la tabella quando cambia la data del filtro o arrivano gli stakeholder
    useEffect(() => {
        if (stakeholders.length > 0) {
            fetchActivities(filterDate);
        }
    }, [filterDate, stakeholders]);

    const retrieveTables = () => {
        TableService.getAll()
            .then(response => setTables(response.data))
            .catch(e => console.error("Errore tabelle:", e));
    };

    const retrieveStakeholders = (sede) => {
        StakeholderService.getAll(sede)
            .then(response => setStakeholders(response.data))
            .catch(e => console.log(e));
    };

    const fetchActivities = async (date) => {
        if (stakeholders.length === 0) return;
        try {
            const response = await ActionService.findByDate(date, user.sede);
            const formattedActivities = response.data.map(action => {
                const relatedStakeholder = stakeholders.find(sh => String(sh.id) === String(action.contatto_id));
                return {
                    id: action.id || `temp-${Math.random()}`,
                    codice: relatedStakeholder?.codice || '',
                    tipologia: action.tipologia,
                    orario_inizio: action.orario_inizio,
                    orario_fine: action.orario_fine,
                    data: action.data,
                    note: action.note,
                    contatto_id: action.contatto_id,
                    nome: relatedStakeholder?.nome || '',
                    cognome: relatedStakeholder?.cognome || '',
                };
            });
            setTodayActivities(formattedActivities);
        } catch (e) {
            console.error("Errore fetch attività:", e);
            setSnackbarMessage('Errore nel caricamento della tabella.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    const saveAction = async () => {
        if (!Action.data || !startTime || !endTime || !Action.tipologia || Action.contatto_id.length === 0) {
            setSnackbarMessage('Compila tutti i campi obbligatori!');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }

        const baseData = {
            data: Action.data,
            orario_inizio: startTime.format("HH:mm:ss"),
            orario_fine: endTime.format("HH:mm:ss"),
            tipologia: Action.tipologia,
            note: Action.note,
        };

        try {
            const createPromises = Action.contatto_id.map((id) => {
                const selectedStakeholder = stakeholders.find(sh => sh.id === id);
                if (!selectedStakeholder) return Promise.resolve(null);
                const actionData = {
                    ...baseData,
                    contatto_id: id,
                    nome: selectedStakeholder.nome,
                    cognome: selectedStakeholder.cognome,
                    codice: selectedStakeholder.codice,
                    categoria_sh: selectedStakeholder.categoria
                };
                return ActionService.create(actionData);
            });

            await Promise.all(createPromises);
            setSnackbarMessage('Azioni inserite con successo!');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);

            // Aggiorna la tabella (se la data di inserimento corrisponde alla data visualizzata)
            fetchActivities(filterDate);

            // Reset form
            setAction(initialActionState);
            setStartTime(dayjs().hour(0).minute(0).second(0));
            setEndTime(dayjs().hour(0).minute(0).second(0));

        } catch (e) {
            console.error("Errore salvataggio:", e);
            setSnackbarMessage('Errore durante l\'inserimento.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    const updateAction = async () => {
        try {
            const actionToUpdate = {
                data: editFormData.data,
                orario_inizio: editStartTime.format("HH:mm:ss"),
                orario_fine: editEndTime.format("HH:mm:ss"),
                tipologia: editFormData.tipologia,
                note: editFormData.note,
                contatto_id: editFormData.contatto_id[0],
            };
            await ActionService.update(editFormData.id, actionToUpdate);
            setSnackbarMessage('Azione modificata!');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
            setEditDialogOpen(false);
            fetchActivities(filterDate);
        } catch (e) {
            console.error(e);
        }
    };

    const confirmDelete = async () => {
        try {
            await ActionService.remove(currentActionEdit.id);
            setSnackbarMessage('Azione cancellata!');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
            setEditDialogOpen(false);
            setConfirmDeleteDialogOpen(false);
            fetchActivities(filterDate);
        } catch (e) {
            console.error(e);
        }
    };

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setAction({ ...Action, [name]: value });
    };

    const handleEditInputChange = (event) => {
        const { name, value } = event.target;
        setEditFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleContactChange = (event, value) => {
        setAction({ ...Action, contatto_id: value.map((val) => val.id) });
    };

    const handleRowDoubleClick = (params) => {
        const actionData = params.row;
        setCurrentActionEdit(actionData);
        setEditFormData({
            id: actionData.id,
            data: dayjs(actionData.data).format("YYYY-MM-DD"),
            tipologia: actionData.tipologia,
            note: actionData.note,
            contatto_id: [actionData.contatto_id],
        });
        setEditStartTime(dayjs(`2000-01-01T${actionData.orario_inizio}`));
        setEditEndTime(dayjs(`2000-01-01T${actionData.orario_fine}`));
        setEditDialogOpen(true);
    };

    const columns = [
        { field: 'id', headerName: 'ID', width: 80 },
        { field: 'nome', headerName: 'Nome', width: 150 },
        { field: 'cognome', headerName: 'Cognome', width: 150 },
        { field: 'codice', headerName: 'Codice Contatto', width: 150 },
        { field: 'tipologia', headerName: 'Tipologia Attività', width: 200 },
        { field: 'orario_inizio', headerName: 'Ora Inizio', width: 130 },
        { field: 'orario_fine', headerName: 'Ora Fine', width: 130 },
    ];

    const tipologie = [
        { sigla: 'IR', descrizione: 'Chiamate di soci in entrata' },
        { sigla: 'OR', descrizione: 'Chiamate ai soci in uscita' },
        { sigla: 'JS', descrizione: 'Lavoro in Job Station' },
        { sigla: 'VOL', descrizione: 'Volontariato' },
        { sigla: 'WESE', descrizione: 'Weekend/Vacanze/Serali' },
        { sigla: 'WOD', descrizione: 'Work Order Demand' },
        { sigla: 'LTT', descrizione: 'Lavoro di transizione' }
    ];

    return (
        <Paper elevation={3} sx={{ p: 3, m: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Inserisci una nuova attività</Typography>
            <Box component="form" sx={{ flexGrow: 1 }} noValidate>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField label="Data Azione" name="data" type="date" InputLabelProps={{ shrink: true }} value={Action.data || ''} onChange={handleInputChange} fullWidth />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <TimeField label="Orario inizio" value={startTime} onChange={setStartTime} format="HH:mm" fullWidth />
                        </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <TimeField label="Orario fine" value={endTime} onChange={setEndTime} format="HH:mm" fullWidth />
                        </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <FormControl fullWidth>
                            <InputLabel>Tipologia</InputLabel>
                            <Select name="tipologia" value={Action.tipologia || ''} onChange={handleInputChange} label="Tipologia" renderValue={(selected) => selected}>
                                {tipologie.map((tipo) => (
                                    <MenuItem key={tipo.sigla} value={tipo.sigla} sx={{ py: 1, borderBottom: '1px solid #eee' }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>{tipo.sigla}</Typography>
                                            <Typography variant="caption" color="text.secondary">{tipo.descrizione}</Typography>
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={12} md={8}>
                        <Autocomplete
                            multiple options={stakeholders} disableCloseOnSelect
                            getOptionLabel={(o) => admin ? `${o.cognome} ${o.nome} - ${o.codice}` : (o.codice || '')}
                            value={stakeholders.filter(s => Action.contatto_id.includes(s.id))}
                            onChange={handleContactChange}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            renderOption={(props, option, { selected }) => (
                                <li {...props} key={option.id}>
                                    <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
                                    {admin ? `${option.cognome} ${option.nome} - ${option.codice}` : option.codice}
                                </li>
                            )}
                            renderInput={(params) => <TextField {...params} label="Contatti" placeholder="Cerca" />}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField label="Note" name="note" value={Action.note} fullWidth multiline rows={2} onChange={handleInputChange} />
                    </Grid>
                    <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button variant="contained" color="success" onClick={saveAction}>Salva Attività</Button>
                    </Grid>
                </Grid>
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* SEZIONE VISUALIZZAZIONE TABELLA CON FILTRO DATA */}
            <Box sx={{ mt: 2 }}>
                <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="h6">
                            Attività del {dayjs(filterDate).format("DD/MM/YYYY")}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <TextField
                            label="Visualizza data"
                            type="date"
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            value={filterDate}
                            onChange={(e) => setFilterDate(e.target.value)}
                            sx={{ width: 220 }}
                        />
                        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => fetchActivities(filterDate)}>
                            Aggiorna
                        </Button>
                    </Grid>
                </Grid>

                <div style={{ height: 400, width: '100%' }}>
                    <DataGrid
                        rows={todayActivities}
                        columns={columns}
                        pageSizeOptions={[5, 10]}
                        initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
                        onRowDoubleClick={handleRowDoubleClick}
                        columnVisibilityModel={columnVisibilityModel}
                    />
                </div>
            </Box>

            <Snackbar open={snackbarOpen} autoHideDuration={4000} onClose={() => setSnackbarOpen(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity}>{snackbarMessage}</Alert>
            </Snackbar>

            {/* Dialog Edit */}
            {editDialogOpen && (
                <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} fullWidth>
                    <DialogTitle>Modifica Azione</DialogTitle>
                    <DialogContent>
                        <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                            <Typography variant="caption" color="text.secondary">Contatto selezionato:</Typography>
                            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                {admin
                                    ? `${currentActionEdit?.cognome} ${currentActionEdit?.nome} - ${currentActionEdit?.codice}`
                                    : `Codice: ${currentActionEdit?.codice}`
                                }
                            </Typography>
                        </Box>
                        <TextField label="Data" type="date" name="data" value={editFormData.data} onChange={handleEditInputChange} fullWidth margin="normal" InputLabelProps={{ shrink: true }} />
                        <Select name="tipologia" value={editFormData.tipologia} onChange={handleEditInputChange} fullWidth margin="normal" label="Tipologia">
                            {tipologie.map((tipo) => (
                                <MenuItem key={tipo.sigla} value={tipo.sigla} sx={{ py: 1, borderBottom: '1px solid #eee' }}>{tipo.sigla}</MenuItem>
                            ))}
                        </Select>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <TimeField label="Inizio" value={editStartTime} onChange={setEditStartTime} format="HH:mm" fullWidth margin="normal" />
                            <TimeField label="Fine" value={editEndTime} onChange={setEditEndTime} format="HH:mm" fullWidth margin="normal" />
                        </LocalizationProvider>
                        <TextField label="Note" name="note" value={editFormData.note} onChange={handleEditInputChange} fullWidth multiline rows={3} margin="normal" />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setEditDialogOpen(false)}>Annulla</Button>
                        <Button onClick={() => setConfirmDeleteDialogOpen(true)} color="error" disabled={!admin}>Elimina</Button>
                        <Button onClick={updateAction} color="success">Salva</Button>
                    </DialogActions>
                </Dialog>
            )}

            {/* Dialog Delete */}
            <Dialog open={confirmDeleteDialogOpen} onClose={() => setConfirmDeleteDialogOpen(false)}>
                <DialogTitle>Conferma</DialogTitle>
                <DialogContent><DialogContentText>Eliminare definitivamente questa attività?</DialogContentText></DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDeleteDialogOpen(false)}>No</Button>
                    <Button onClick={confirmDelete} color="error">Sì, Elimina</Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default InsertAction;